import axios, { AxiosRequestConfig, AxiosResponse, ResponseType } from "axios";
import { Middleware } from "@reduxjs/toolkit";

interface ApiMeta {
  method: string;
  url: string;
  data?: any;
  headers?: Record<string, string>;
  responseType?: ResponseType;
}

interface AuthMeta {
  token?: string;
  error?: any;
}

interface ApiAction {
  type: string;
  meta?: {
    api?: ApiMeta;
    auth?: AuthMeta;
  };
  payload?: {
    resolve?: boolean;
  };
  response?: any;
  error?: any;
}

const apiMiddleware =
  (config: any): Middleware =>
  (store) =>
  (next) =>
  (action: ApiAction) => {
    if (action.type === "API_ERRORS") {
      next(action);
      return Promise.reject(action.error);
    }

    if (!(action.meta && action.meta.api)) {
      next(action);
      return Promise.resolve("[Api Middleware] Skipping API call since API meta not available");
    }

    const { method, url, data, headers, responseType } = action.meta.api;

    const auth = action.meta.auth;

    if (auth?.error && !auth?.token) {
      next(action);
      return Promise.reject(auth.error);
    }

    return new Promise((resolve, reject) => {
      if (!config) {
        console.log(
          "[Api Middleware] Config unavailable. The AD package requires config to get the authorization token for the api call."
        );
      }

      const needsJSON = ["post", "put", "patch"].includes(method.toLowerCase());

      const customHeaders: Record<string, string> = headers || {};

      if (auth?.token) {
        customHeaders.Authorization = `Bearer ${auth.token}`;
      }

      customHeaders["Content-Type"] = needsJSON ? "application/json" : "";

      const axiosConfig: AxiosRequestConfig = {
        method,
        url,
        data,
        headers: customHeaders,
      };

      if (responseType) {
        axiosConfig.responseType = responseType;
      }

      axios(axiosConfig)
        .then((response: AxiosResponse) => {
          action.response = response.data;

          next(action);

          if (action.payload?.resolve) {
            return resolve(response.data);
          }

          return resolve(undefined);
        })
        .catch((error: any) => {
          console.log("[Api Middleware Error]", error);

          next({
            type: "API_ERRORS",
            error,
          });

          return reject(error);
        });
    });
  };

export { apiMiddleware };
